import type { CSSProperties } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { DayPicker, type DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover } from '@base-ui/react/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CustomDates } from '@/pages/dashboard/use-dashboard-metrics';

function toDate(s: string): Date | undefined {
  if (!s) return undefined;
  const d = parseISO(s);
  return isValid(d) ? d : undefined;
}

// Theme react-day-picker's accent to the app's primary token so the selected
// range matches the rest of the UI in either light or dark mode, and shrink
// the default 44px cells to app scale (the font already inherits via `font: inherit`).
const accentVars = {
  '--rdp-accent-color': 'var(--primary)',
  '--rdp-accent-background-color': 'color-mix(in oklab, var(--primary) 20%, transparent)',
  // Range endpoints get a primary background; default text is hardcoded white,
  // so use the contrasting primary-foreground token instead of white-on-white.
  '--rdp-range_start-color': 'var(--primary-foreground)',
  '--rdp-range_end-color': 'var(--primary-foreground)',
  '--rdp-day-width': '2rem',
  '--rdp-day-height': '2rem',
} as CSSProperties;

// The stylesheet sizes these with absolute keywords (large/small); inline styles
// override it so the calendar matches the app's text-sm/text-xs scale.
const dpStyles = {
  caption_label: { fontSize: '0.875rem', fontWeight: 500 },
  weekday: { fontSize: '0.75rem', fontWeight: 400 },
  day: { fontSize: '0.9375rem' },
};

/**
 * One field for a custom date range, replacing the separate start/end inputs.
 * Reads/writes the same `{ start, end }` YYYY-MM-DD shape the dashboards use.
 */
export function DateRangePicker({
  value,
  onChange,
  className,
}: {
  value: CustomDates;
  onChange: (v: CustomDates) => void;
  className?: string;
}) {
  const from = toDate(value.start);
  const to = toDate(value.end);
  const selected: DateRange | undefined = from ? { from, to } : undefined;
  const label = from
    ? to
      ? `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`
      : format(from, 'MMM d, yyyy')
    : 'Pick a date range';

  return (
    <Popover.Root>
      <Popover.Trigger
        render={
          <Button variant="outline" className={cn('w-auto justify-start gap-2 font-normal', className)} />
        }
      >
        <CalendarIcon className="size-4" />
        {label}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner positionMethod="fixed" className="isolate z-50 outline-none" align="start" sideOffset={4}>
          <Popover.Popup className="z-50 rounded-lg bg-popover p-3 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none">
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={selected}
              onSelect={(r) =>
                onChange({
                  start: r?.from ? format(r.from, 'yyyy-MM-dd') : '',
                  end: r?.to ? format(r.to, 'yyyy-MM-dd') : '',
                })
              }
              style={accentVars}
              styles={dpStyles}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
